import rough from "roughjs/bin/rough";

import { THEME, arrayToMap, toBrandedType } from "@excalidraw/common";
import {
  doBoundsIntersect,
  getElementBounds,
  getElementsOverlappingFrame,
  getInitializedImageElements,
  getTextureFileId,
  syncInvalidIndices,
  updateImageCache,
} from "@excalidraw/element";
import { getDefaultAppState } from "@excalidraw/excalidraw/appState";
import { Fonts } from "@excalidraw/excalidraw/fonts";
import { renderStaticScene } from "@excalidraw/excalidraw/renderer/staticScene";

import type {
  ExcalidrawFrameLikeElement,
  NonDeletedExcalidrawElement,
  NonDeletedSceneElementsMap,
} from "@excalidraw/element/types";
import type { RenderableElementsMap } from "@excalidraw/excalidraw/scene/types";
import type { AppState, BinaryFiles } from "@excalidraw/excalidraw/types";

import { AnimationRuntime } from "../runtime";
import {
  cameraAt,
  cameraOverscan,
  drawCameraFrame,
  getScreenCamera,
} from "../camera";
import { drawSmartFrame, matchElements } from "../smartAnimate";
import {
  drawTransitionFrame,
  getScreenTransition,
  rectUnion,
  transitionProgressPlan,
  travelExportViews,
  travelRectAt,
} from "../transitions";

import { requestExport } from "../../export-gate";

import { createEncoder, yieldToEventLoop } from "./encoders";

import type { Box, SmartItem } from "../smartAnimate";

import type { EncoderOptions, ExportFormat } from "./encoders";

export const MAX_EXPORT_FRAMES = 1800;
export const MAX_EXPORT_AREA = 4096 * 2304;

// H.264/VP9 need even dimensions; the encoder configures itself with
// width - width % 2, so frames must match or WebCodecs rejects/rescales them.
// One shaved pixel is invisible in GIF/APNG too, so apply it everywhere.
// (min 2 so a degenerate frame can't produce a zero-sized canvas)
const evenFloor = (n: number) => Math.max(2, Math.floor(n) & ~1);

export type RecordOptions = {
  format: ExportFormat;
  fps: number;
  scale: number;
  /** fill behind the frame — any CSS color, or "transparent" (APNG only)
   * to skip the fill; defaults to the canvas background */
  background?: string;
  onProgress?: (done: number, total: number) => void;
} & EncoderOptions;

/**
 * One-time render setup for a frame (fonts, image cache, canvas), mirroring
 * scene/export.ts#exportToCanvas — inlined so the per-sample loop only
 * seeks the timeline and re-renders into the same canvas.
 * ponytail: duplicates ~30 lines of exportToCanvas setup; fold back into the
 * package if exportToCanvas ever grows a reusable "prepared renderer" API.
 */
const createFrameRenderer = async ({
  elements,
  appState,
  files,
  frame,
  scale,
  background,
  only,
}: {
  elements: readonly NonDeletedExcalidrawElement[];
  appState: AppState;
  files: BinaryFiles;
  frame: ExcalidrawFrameLikeElement;
  scale: number;
  background: string;
  /** render exactly these elements (smart-animate per-element snapshots) —
   * caller must include bound text children */
  only?: readonly NonDeletedExcalidrawElement[];
}) => {
  const elementsForRender =
    only ?? getElementsOverlappingFrame(elements, frame, arrayToMap(elements));

  await Fonts.loadElementsFonts(elementsForRender);

  // ponytail: collect all fileIds including texture effects so they render
  // with proper images during export. Texture effects contain fileId just like
  // image elements but weren't being included in the cache.
  const imageFileIds = getInitializedImageElements(elementsForRender).map(
    (element) => element.fileId,
  );
  const textureFileIds = elementsForRender
    .map((el) => getTextureFileId(el))
    .filter((id) => id !== null);

  const { imageCache } = await updateImageCache({
    imageCache: new Map(),
    fileIds: [...imageFileIds, ...textureFileIds],
    files,
  });

  const canvas = document.createElement("canvas");
  canvas.width = evenFloor(frame.width * scale);
  canvas.height = evenFloor(frame.height * scale);
  const rc = rough.canvas(canvas);

  const defaultAppState = getDefaultAppState();
  const elementsMap = toBrandedType<RenderableElementsMap>(
    arrayToMap(elementsForRender),
  );
  const allElementsMap = toBrandedType<NonDeletedSceneElementsMap>(
    arrayToMap(syncInvalidIndices(elements)),
  );

  const renderAppState = {
    ...appState,
    // the artwork's own colors, never the UI theme (see staticScene.ts) — the
    // recorded video must match what's on the canvas
    theme: THEME.LIGHT,
    // renderStaticScene fills from appState.viewBackgroundColor (not
    // renderConfig.canvasBackgroundColor), so the export background must be
    // overridden here; bootstrapCanvas clearRects for "transparent"
    viewBackgroundColor: background,
    // transparent export: also skip the frame's own artboard fill
    // (renderFrameSurfaceLayer), or the output stays opaque; with outline/
    // name/clip already off, `enabled` gates nothing else in this path
    frameRendering: {
      enabled: background !== "transparent",
      outline: false,
      name: false,
      clip: false,
    },
    scrollX: -frame.x,
    scrollY: -frame.y,
    zoom: defaultAppState.zoom,
    shouldCacheIgnoreZoom: false,
    selectedElementIds: {},
  };

  const render = () =>
    renderStaticScene({
      canvas,
      rc,
      elementsMap,
      allElementsMap,
      visibleElements: elementsForRender,
      scale,
      appState: renderAppState as typeof appState,
      renderConfig: {
        // bootstrapCanvas clearRects (no fill) for "transparent"
        canvasBackgroundColor: background,
        imageCache,
        renderGrid: false,
        isExporting: true,
        embedsValidationStatus: new Map(),
        elementsPendingErasure: new Set(),
        pendingFlowchartNodes: null,
        theme: THEME.LIGHT,
      },
    });

  return { canvas, render, elementsForRender };
};

/** One rendered still of a screen at its rest/current-override state — the
 * live-playback transition overlay's input (export renders its own, sampled
 * precisely; playback accepts the rest-state approximation). */
export const renderScreenSnapshot = async ({
  elements,
  appState,
  files,
  frame,
  background,
  scale = 1,
}: {
  elements: readonly NonDeletedExcalidrawElement[];
  appState: AppState;
  files: BinaryFiles;
  frame: ExcalidrawFrameLikeElement;
  background: string;
  scale?: number;
}): Promise<HTMLCanvasElement> => {
  const { canvas, render } = await createFrameRenderer({
    elements,
    appState,
    files,
    frame,
    scale,
    background,
  });
  render();
  return canvas;
};

const SNAPSHOT_PADDING = 32; // stroke/roughness/shadow overshoot, scene units

/**
 * Smart Animate (audit L6 subset): matches elements between two screens and
 * returns a draw closure — pairs morph (box lerp + snapshot crossfade),
 * unmatched fade. Elements render at their current override state: for the
 * outgoing screen that's its final sampled frame, for the incoming one its
 * rest state — the same approximation the Phase-3 overlay makes.
 */
export const prepareSmartTransition = async ({
  elements,
  appState,
  files,
  fromFrame,
  toFrame,
  scale,
  width,
  height,
  background,
}: {
  elements: readonly NonDeletedExcalidrawElement[];
  appState: AppState;
  files: BinaryFiles;
  fromFrame: ExcalidrawFrameLikeElement;
  toFrame: ExcalidrawFrameLikeElement;
  scale: number;
  width: number;
  height: number;
  background: string;
}): Promise<(ctx: CanvasRenderingContext2D, progress: number) => void> => {
  const elementsMap = arrayToMap(elements);
  const fromEls = getElementsOverlappingFrame(elements, fromFrame, elementsMap);
  const toEls = getElementsOverlappingFrame(elements, toFrame, elementsMap);
  const { pairs, exits, enters } = matchElements(
    fromEls,
    fromFrame,
    toEls,
    toFrame,
  );

  // each screen renders centered on the shared output canvas — element boxes
  // must land where recordScreens draws that screen
  const offsetFor = (frame: ExcalidrawFrameLikeElement) => ({
    x: Math.floor((width - evenFloor(frame.width * scale)) / 2),
    y: Math.floor((height - evenFloor(frame.height * scale)) / 2),
  });

  const snap = async (
    source: NonDeletedExcalidrawElement,
    // pairs snapshot angle-0 so the compositor can rotate geometrically
    zeroAngle = false,
  ) => {
    const el = (
      zeroAngle && "angle" in source && source.angle
        ? { ...source, angle: 0 }
        : source
    ) as NonDeletedExcalidrawElement;
    const [x1, y1, x2, y2] = getElementBounds(el, elementsMap);
    const pseudo = {
      x: x1 - SNAPSHOT_PADDING,
      y: y1 - SNAPSHOT_PADDING,
      width: x2 - x1 + SNAPSHOT_PADDING * 2,
      height: y2 - y1 + SNAPSHOT_PADDING * 2,
    } as ExcalidrawFrameLikeElement;
    const boundText = (el.boundElements ?? [])
      .filter((b) => b.type === "text")
      .map((b) => elementsMap.get(b.id))
      .filter(Boolean)
      .map((t) =>
        zeroAngle ? { ...t, angle: 0 } : t,
      ) as NonDeletedExcalidrawElement[];
    const { canvas, render } = await createFrameRenderer({
      elements,
      appState,
      files,
      frame: pseudo,
      scale,
      background: "transparent",
      only: [el, ...boundText],
    });
    render();
    return { canvas, bounds: pseudo };
  };

  const outBox = (
    bounds: { x: number; y: number; width: number; height: number },
    frame: ExcalidrawFrameLikeElement,
  ): Box => {
    const off = offsetFor(frame);
    return {
      x: (bounds.x - frame.x) * scale + off.x,
      y: (bounds.y - frame.y) * scale + off.y,
      w: bounds.width * scale,
      h: bounds.height * scale,
    };
  };

  const items: SmartItem[] = [];
  for (const [a, b] of pairs) {
    const sa = await snap(a, true);
    const sb = await snap(b, true);
    items.push({
      kind: "pair",
      fromImg: sa.canvas,
      toImg: sb.canvas,
      from: outBox(sa.bounds, fromFrame),
      to: outBox(sb.bounds, toFrame),
      fromAngle: ("angle" in a ? a.angle : 0) || 0,
      toAngle: ("angle" in b ? b.angle : 0) || 0,
    });
  }
  for (const el of exits) {
    const s = await snap(el);
    items.push({
      kind: "exit",
      img: s.canvas,
      box: outBox(s.bounds, fromFrame),
    });
  }
  for (const el of enters) {
    const s = await snap(el);
    items.push({
      kind: "enter",
      img: s.canvas,
      box: outBox(s.bounds, toFrame),
    });
  }

  return (ctx, progress) => {
    if (background === "transparent") {
      ctx.clearRect(0, 0, width, height);
    } else {
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, width, height);
    }
    drawSmartFrame(ctx, items, progress);
  };
};

/**
 * Travel transition: pre-renders the swept corridor (bounding union of the
 * two endpoint views) once, then blits the interpolated view per encoder
 * frame, clipped to the interpolated FRAME rect's projection (background
 * bars fill the rest). Endpoint views come from travelExportViews and the
 * clip collapses onto the full frame at p=0/p=1, so those frames match the
 * adjacent screen stills exactly (up to sub-pixel rounding); mid-travel
 * shows real canvas content through the gliding frame-sized window.
 * Corridor content renders at rest state — same approximation the other
 * snapshot transitions make.
 */
export const prepareTravelTransition = async ({
  elements,
  appState,
  files,
  fromFrame,
  toFrame,
  scale,
  width,
  height,
  background,
}: {
  elements: readonly NonDeletedExcalidrawElement[];
  appState: AppState;
  files: BinaryFiles;
  fromFrame: ExcalidrawFrameLikeElement;
  toFrame: ExcalidrawFrameLikeElement;
  scale: number;
  width: number;
  height: number;
  background: string;
}): Promise<(ctx: CanvasRenderingContext2D, progress: number) => void> => {
  const views = travelExportViews(fromFrame, toFrame, width, height, scale);
  const corridor = rectUnion(views.from, views.to);
  // ponytail: cap the corridor render so far-apart screens can't blow the
  // canvas size limit — the blit upscales (slight blur on long travels);
  // tiled corridor rendering is the upgrade if that ever reads badly.
  // area-capped at 4096², Safari's canvas limit is ~16.7M px
  const MAX_CORRIDOR_DIM = 8192;
  const MAX_CORRIDOR_AREA = 4096 * 4096;
  const innerScale = Math.min(
    scale,
    Math.sqrt(
      MAX_CORRIDOR_AREA / Math.max(1, corridor.width * corridor.height),
    ),
    MAX_CORRIDOR_DIM / Math.max(1, corridor.width),
    MAX_CORRIDOR_DIM / Math.max(1, corridor.height),
  );
  // the corridor is a pseudo-frame with no `id`, so createFrameRenderer's
  // default getElementsOverlappingFrame(elements, corridor, ...) filter
  // (`!el.frameId || el.frameId === frame.id`) would drop every element
  // that actually belongs to a real screen — i.e. the screens' own content —
  // since none of them have a frameId matching an id that doesn't exist.
  // Select geometrically instead (bounds-overlap only, no frame-membership
  // check); this also naturally sweeps up free elements between screens and
  // the frame elements themselves so their artboards render.
  const elementsMap = arrayToMap(elements);
  const corridorElements = elements.filter((el) =>
    doBoundsIntersect(getElementBounds(el, elementsMap), [
      corridor.x,
      corridor.y,
      corridor.x + corridor.width,
      corridor.y + corridor.height,
    ]),
  );
  const { canvas, render } = await createFrameRenderer({
    elements,
    appState,
    files,
    frame: corridor as ExcalidrawFrameLikeElement,
    scale: innerScale,
    background,
    only: corridorElements,
  });
  render();
  // the corridor canvas is evenFloor(corridor.dim * innerScale) — a couple
  // px off raw innerScale — so the blit SOURCE rect must use the canvas's
  // actual per-axis scale or it overruns the bitmap at the far corridor edge
  const sx = canvas.width / corridor.width;
  const sy = canvas.height / corridor.height;
  return (ctx, progress) => {
    if (background === "transparent") {
      ctx.clearRect(0, 0, width, height);
    } else {
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, width, height);
    }
    const view = travelRectAt(views.from, views.to, progress);
    // clip to the gliding frame's window — outside it the screen stills show
    // background bars, so the travel frame must too or the endpoints pop
    const frameRect = travelRectAt(fromFrame, toFrame, progress);
    ctx.save();
    ctx.beginPath();
    ctx.rect(
      (frameRect.x - view.x) * scale,
      (frameRect.y - view.y) * scale,
      frameRect.width * scale,
      frameRect.height * scale,
    );
    ctx.clip();
    ctx.drawImage(
      canvas,
      (view.x - corridor.x) * sx,
      (view.y - corridor.y) * sy,
      view.width * sx,
      view.height * sy,
      0,
      0,
      width,
      height,
    );
    ctx.restore();
  };
};

export type ScreenPlan = {
  frame: ExcalidrawFrameLikeElement;
  /** configured screen time; the screen holds at least this long, longer if
   * its element animations outlast it */
  durationMs: number;
};

/**
 * Renders a sequence of frames into one animation — the single export entry
 * point. A screen, a whole flipbook and a lone frame are all just different
 * `screens` lists (see export/targets.ts), so there is no second recorder to
 * keep in sync; a static frame is a one-sample screen, not an error.
 *
 * All screens share a single output canvas sized to the largest frame (smaller
 * frames render centered on the canvas background), so GIF/video dimensions
 * stay constant.
 */
export const recordScreens = async ({
  elements,
  appState,
  files,
  screens,
  format,
  fps,
  scale,
  background,
  loopForever,
  quality,
  onProgress,
}: {
  elements: readonly NonDeletedExcalidrawElement[];
  appState: AppState;
  files: BinaryFiles;
  screens: readonly ScreenPlan[];
} & RecordOptions): Promise<Blob> => {
  if (screens.length === 0) {
    throw new Error("No frames to export.");
  }
  const width = evenFloor(
    Math.max(...screens.map((s) => s.frame.width)) * scale,
  );
  const height = evenFloor(
    Math.max(...screens.map((s) => s.frame.height)) * scale,
  );
  if (width * height > MAX_EXPORT_AREA) {
    throw new Error(
      "Export resolution is too large. Reduce the scale or frame size.",
    );
  }
  // both checks above are pure client-side math (no canvas/runtime work) —
  // don't burn a slot on a request that was always going to be rejected
  // locally. MAX_EXPORT_FRAMES below sits after the plan pass (real per-frame
  // compile work), so it correctly stays gated.
  await requestExport(format);
  const frameDurationMs = 1000 / fps;
  const bg = background ?? appState.viewBackgroundColor;
  const elementsMap = arrayToMap(elements);
  const runtime = new AnimationRuntime();
  try {
    // plan pass: effective screen length = max(configured, element timeline);
    // a screen after the first may carry a transition INTO it (audit 4.4)
    const plans = screens.map(({ frame, durationMs }, index) => {
      const frameElements = getElementsOverlappingFrame(
        elements,
        frame,
        elementsMap,
      );
      runtime.compile(frameElements);
      const timelineMs = runtime.getDurationMs();
      const effectiveMs = Math.max(durationMs, timelineMs);
      const transition = index > 0 ? getScreenTransition(frame) : null;
      return {
        frame,
        frameElements,
        timelineMs,
        transition,
        transitionPlan: transition
          ? transitionProgressPlan(transition.durationMs, fps)
          : [],
        sampleCount: Math.max(1, Math.ceil(effectiveMs / frameDurationMs)),
      };
    });
    const totalFrames = plans.reduce(
      (sum, p) => sum + p.sampleCount + p.transitionPlan.length,
      0,
    );
    if (totalFrames > MAX_EXPORT_FRAMES) {
      throw new Error(
        `Sequence is too long to export: ${totalFrames} frames at ${fps}fps (max ${MAX_EXPORT_FRAMES}). Reduce screen durations, repeats, or fps.`,
      );
    }

    const output = document.createElement("canvas");
    output.width = width;
    output.height = height;
    const ctx = output.getContext("2d");
    if (!ctx) {
      throw new Error("Could not create export canvas.");
    }
    const encoder = await createEncoder(format, width, height, fps, {
      loopForever,
      quality,
    });

    let done = 0;
    // the previous screen's final composed frame — the "from" side of a
    // transition into the next screen
    let prevSnapshot: HTMLCanvasElement | null = null;
    let prevFrame: ExcalidrawFrameLikeElement | null = null;
    for (const plan of plans) {
      runtime.compile(plan.frameElements);
      const camera = getScreenCamera(plan.frame);
      // camera screens render overscanned so punch-ins never pixelate
      const { canvas, render } = await createFrameRenderer({
        elements,
        appState,
        files,
        frame: plan.frame,
        scale: scale * (camera ? cameraOverscan(camera) : 1),
        background: bg,
      });
      const baseW = evenFloor(plan.frame.width * scale);
      const baseH = evenFloor(plan.frame.height * scale);
      // paints the screen's current render onto a destination context —
      // through the camera when one is configured, else centered
      const composeSample = (
        dest: CanvasRenderingContext2D,
        progress: number,
      ) => {
        if (bg === "transparent") {
          dest.clearRect(0, 0, width, height);
        } else {
          dest.fillStyle = bg;
          dest.fillRect(0, 0, width, height);
        }
        if (camera) {
          drawCameraFrame(dest, canvas, cameraAt(camera, progress), {
            width,
            height,
            baseW,
            baseH,
          });
        } else {
          dest.drawImage(
            canvas,
            Math.floor((width - canvas.width) / 2),
            Math.floor((height - canvas.height) / 2),
          );
        }
      };
      if (plan.transition?.type === "smart" && prevFrame) {
        const drawSmart = await prepareSmartTransition({
          elements,
          appState,
          files,
          fromFrame: prevFrame,
          toFrame: plan.frame,
          scale,
          width,
          height,
          background: bg,
        });
        for (const progress of plan.transitionPlan) {
          drawSmart(ctx, progress);
          await encoder.addFrame(output, frameDurationMs);
          done++;
          onProgress?.(done, totalFrames);
          if (done % 10 === 9) {
            await yieldToEventLoop();
          }
        }
      } else if (plan.transition?.type === "travel" && prevFrame) {
        // known limitation: a travel out of a camera'd screen starts from
        // the neutral view, not the camera's end state — same class of
        // approximation as the other rest-state transitions
        const drawTravel = await prepareTravelTransition({
          elements,
          appState,
          files,
          fromFrame: prevFrame,
          toFrame: plan.frame,
          scale,
          width,
          height,
          background: bg,
        });
        for (const progress of plan.transitionPlan) {
          drawTravel(ctx, progress);
          await encoder.addFrame(output, frameDurationMs);
          done++;
          onProgress?.(done, totalFrames);
          if (done % 10 === 9) {
            await yieldToEventLoop();
          }
        }
      } else if (plan.transition && prevSnapshot) {
        // incoming screen's first sample, composed at output size
        runtime.sampleAt(0);
        render();
        const toSnapshot = document.createElement("canvas");
        toSnapshot.width = width;
        toSnapshot.height = height;
        composeSample(toSnapshot.getContext("2d")!, 0);
        for (const progress of plan.transitionPlan) {
          ctx.clearRect(0, 0, width, height);
          drawTransitionFrame(ctx, prevSnapshot, toSnapshot, {
            type: plan.transition.type,
            direction: plan.transition.direction,
            progress,
            width,
            height,
          });
          await encoder.addFrame(output, frameDurationMs);
          done++;
          onProgress?.(done, totalFrames);
          if (done % 10 === 9) {
            await yieldToEventLoop();
          }
        }
      }
      for (let i = 0; i < plan.sampleCount; i++) {
        runtime.sampleAt(Math.min(i * frameDurationMs, plan.timelineMs));
        render();
        composeSample(
          ctx,
          plan.sampleCount > 1 ? i / (plan.sampleCount - 1) : 1,
        );
        await encoder.addFrame(output, frameDurationMs);
        done++;
        onProgress?.(done, totalFrames);
        if (done % 10 === 9) {
          // let the UI breathe (progress bar, event loop) — MessageChannel
          // yield, exempt from hidden-tab timer throttling
          await yieldToEventLoop();
        }
      }
      // snapshot the last composed frame for the next screen's transition
      prevSnapshot = document.createElement("canvas");
      prevSnapshot.width = width;
      prevSnapshot.height = height;
      prevSnapshot.getContext("2d")!.drawImage(output, 0, 0);
      prevFrame = plan.frame;
    }

    return await encoder.finish();
  } finally {
    // ownership-checked clearAnimationOverrides inside dispose() handles
    // cleanup; don't clobber another runtime's registration (e.g. a live
    // preview) with a raw setAnimationOverrides(null) here.
    runtime.dispose();
  }
};
